import React from 'react';
import { Col, Row, Text } from 'components';
import { FontAwesome5 } from '@expo/vector-icons';
import { StyleSheet } from 'react-native';

const MSC_BLUE = '#004B8D';
const SUCCESS = '#1FA463';
const WARNING = '#F5A623';
const ERROR = '#E94A36';

export interface DeliveryCardData {
  id: string;
  orderNumber: string;
  customer: string;
  driver: string;
  route: string;
  depot: string;
  zone: string;
  status: 'IN_TRANSIT' | 'DELIVERED' | 'MISSING_ITEM' | 'DAMAGED_ITEM';
  emoji: string;
  eta?: string;
  time?: string;
}

interface DeliveryCardProps {
  delivery: DeliveryCardData;
}

const DeliveryCard: React.FC<DeliveryCardProps> = ({ delivery }) => {
  const getStatusColor = (status: DeliveryCardData['status']) => {
    switch (status) {
      case 'IN_TRANSIT':
        return MSC_BLUE;
      case 'DELIVERED':
        return SUCCESS;
      case 'MISSING_ITEM':
        return WARNING;
      case 'DAMAGED_ITEM':
        return ERROR;
      default:
        return MSC_BLUE;
    }
  };

  const getStatusBgColor = (status: DeliveryCardData['status']) => {
    switch (status) {
      case 'IN_TRANSIT':
        return '#E6F2FF';
      case 'DELIVERED':
        return '#E6F7ED';
      case 'MISSING_ITEM':
        return '#FFF8E6';
      case 'DAMAGED_ITEM':
        return '#FFEBE8';
      default:
        return '#E6F2FF';
    }
  };

  const statusColor = getStatusColor(delivery.status);
  const bgColor = getStatusBgColor(delivery.status);
  const isAlert = delivery.status === 'DAMAGED_ITEM';

  return (
    <Col
      style={[
        styles.deliveryCard,
        {
          borderLeftColor: statusColor,
          backgroundColor: bgColor,
        },
        isAlert && styles.pulseAlert,
      ]}
    >
      <Row justifyContent="space-between" alignItems="flex-start" marginBottom={12}>
        <Col flex1>
          <Text fontSize={18} bold color="#1F2937">
            {delivery.orderNumber}
          </Text>
          <Text fontSize={14} color="#4B5563" medium marginTop={4}>
            {delivery.customer}
          </Text>
        </Col>
        <Text fontSize={32}>{delivery.emoji}</Text>
      </Row>

      <Col style={styles.deliveryInfo} marginBottom={12}>
        <Row alignItems="center" marginBottom={4}>
          <FontAwesome5 name="user-tie" size={14} color={statusColor} style={{ marginRight: 8 }} />
          <Text fontSize={14} color="#4B5563">
            Driver: {delivery.driver} • Route: {delivery.route}
          </Text>
        </Row>
        <Row alignItems="center">
          <FontAwesome5 name="map-marker-alt" size={14} color={statusColor} style={{ marginRight: 8 }} />
          <Text fontSize={14} color="#4B5563">
            Depot: {delivery.depot} • Zone: {delivery.zone}
          </Text>
        </Row>
      </Col>

      <Row justifyContent="space-between" alignItems="center">
        <Col
          style={[
            styles.statusBadge,
            {
              backgroundColor: statusColor,
            },
          ]}
        >
          <Text fontSize={12} bold color="white" style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {delivery.status.replace('_', ' ')}
          </Text>
        </Col>
        <Text fontSize={14} bold style={{ color: statusColor }}>
          {delivery.eta ? `ETA: ${delivery.eta}` : delivery.time}
        </Text>
      </Row>
    </Col>
  );
};

const styles = StyleSheet.create({
  deliveryCard: {
    padding: 20,
    borderRadius: 12,
    borderLeftWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  deliveryInfo: {
    // @ts-ignore
    gap: 4,
  },
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 20,
  },
  pulseAlert: {
    opacity: 1,
  },
});

export default DeliveryCard;

